import { createContext, useContext, useMemo } from 'react';
import type { RentalApplication, RentalApplicationListing, UserStatus } from '../models/rental-application';
import type { TermSetStatus } from '@zg-rentals/ts-capps-client';

// Context for when viewing a single application
export const ApplicationContext = createContext<ApplicationContextValue | undefined>(undefined);

export type FeatureFlags = {
  isGuarantorInvitationsEnabled?: boolean;
  hasRequiredDocuments?: boolean;
  hasIncomeDocumentsStep?: boolean;
  hasSupplementalQuestions?: boolean;
};

export const EMPTY_APPLICATION_LISTING: RentalApplicationListing = { rentalListing: null, listingOwner: null };

export interface ApplicationContextValue {
  application: RentalApplication;
  listing: RentalApplicationListing;
  userStatuses: Array<UserStatus>;
  termSetStatus: TermSetStatus;
  featureFlags: FeatureFlags;
  isFirstTimeUser?: boolean;
  requiresTrueUp?: boolean;
}

export function ApplicationContextProvider({
  application,
  listing,
  userStatuses,
  termSetStatus,
  featureFlags,
  isFirstTimeUser,
  requiresTrueUp,
  children,
}: React.PropsWithChildren<ApplicationContextValue>) {
  const value = useMemo(
    () => ({
      application,
      listing,
      userStatuses,
      featureFlags,
      termSetStatus,
      isFirstTimeUser: !!isFirstTimeUser,
      requiresTrueUp: !!requiresTrueUp,
    }),
    [application, listing, userStatuses, featureFlags, isFirstTimeUser, requiresTrueUp, termSetStatus],
  );
  return <ApplicationContext.Provider value={value}>{children}</ApplicationContext.Provider>;
}

export function useApplicationContext() {
  const contextValue = useContext(ApplicationContext);
  if (!contextValue) {
    throw new Error('Application data needs to be loaded first');
  }
  return contextValue;
}

export function useApplicationContextOrUndefined() {
  return useContext(ApplicationContext);
}

export function useApplicationListing() {
  const { listing } = useApplicationContext();
  return listing;
}

export function useFeatureFlags() {
  const { featureFlags } = useApplicationContext();
  return featureFlags;
}
